import { DateTime } from 'luxon'
import { dateRangeWindows, datesForRelativeWindow } from 'utils/dateUtils'

const MONTH_FORMAT = 'yyyy-MM'
const DATE_FORMAT = 'yyyy-MM-dd'
const WINDOW_LABEL_FORMAT = 'LLL yyyy'

const formatSignedDollars = (amountInDollars, formatAbsolute) => {
  const amount = amountInDollars || 0
  const formatted = formatAbsolute(Math.abs(amount))

  return amount < 0 && formatted !== '$0' ? `-${formatted}` : formatted
}

export const formatCurrency = amountInDollars =>
  formatSignedDollars(
    amountInDollars,
    amount => `$${Math.round(amount).toLocaleString()}`,
  )

export const formatCompactCurrency = amountInDollars =>
  formatSignedDollars(amountInDollars, amount =>
    amount >= 1000
      ? `$${Number((amount / 1000).toFixed(1))}k`
      : `$${Math.round(amount)}`,
  )

export const formatDecimal = value =>
  (value || 0).toLocaleString(undefined, {
    minimumFractionDigits: 0,
    maximumFractionDigits: 1,
  })

export const formatPercent = value => `${formatDecimal(value)}%`

export const formatOptionalDecimal = value =>
  value == null ? '–' : formatDecimal(value)

export const formatOptionalPercent = value =>
  value == null ? '–' : formatPercent(value)

// The backend returns null for the benchmark fields (and gross earnings) unless
// its take-home flag is on for the coach.
export const hasTakeHome = benchmarks => benchmarks?.fullTimeBarDollars != null

export const formatFullTimeLabel = benchmarks =>
  hasTakeHome(benchmarks)
    ? `Progress toward ${formatCurrency(
      benchmarks.fullTimeBarDollars,
    )} full-time`
    : 'Progress toward full-time'

export const formatCount = (count, singular, plural = `${singular}s`) =>
  `${count.toLocaleString()} ${count === 1 ? singular : plural}`

// Orders ranked-list entries most to least by `getCount`, then by revenue
// (highest first), then by title A–Z. Returns a copy; the input is untouched.
export const sortByCountRevenueTitle = (
  entries,
  { getCount, getRevenue, getTitle },
) =>
  [...(entries || [])].sort(
    (a, b) =>
      (getCount(b) || 0) - (getCount(a) || 0) ||
      (getRevenue(b) || 0) - (getRevenue(a) || 0) ||
      (getTitle(a) || '').localeCompare(getTitle(b) || '', undefined, {
        sensitivity: 'base',
      }),
  )

export const formatEventPrice = priceInCents => {
  if (priceInCents == null) return null
  if (priceInCents === 0) return 'Free'

  const dollars = priceInCents / 100

  return `$${dollars.toLocaleString(undefined, {
    minimumFractionDigits: priceInCents % 100 === 0 ? 0 : 2,
    maximumFractionDigits: 2,
  })}`
}

// e.g. "Oct 12, 2026 · 9:00 AM · $97", shown in the event's own timezone
export const formatEventDetail = ({
  startDateTime,
  timezone,
  priceInCents,
}) => {
  const start = DateTime.fromISO(startDateTime || '', {
    zone: timezone || undefined,
  })
  const parts = [
    start.isValid ? start.toFormat('LLL d, yyyy') : null,
    start.isValid ? start.toFormat('h:mm a') : null,
    formatEventPrice(priceInCents),
  ]

  return parts.filter(Boolean).join(' · ')
}

export const formatMonthLabel = (month, format = 'LLL yyyy') => {
  const parsed = DateTime.fromFormat(month || '', MONTH_FORMAT)

  return parsed.isValid ? parsed.toFormat(format) : month
}

export const DEFAULT_RANGE_ID = 'this-year'
export const CUSTOM_RANGE_ID = 'custom'
export const MAX_CUSTOM_RANGE_MONTHS = 24
export const MAX_CUSTOM_RANGE_LOOKBACK_MONTHS = 60

export const RANGE_PARAMS = { preset: 'range', start: 'start', end: 'end' }

// The presets are the app's standard relative windows (`utils/dateUtils`),
// widened to whole months because every chart plots monthly buckets. The week
// windows are left out: a week is never more than one bucket.
const PRESETS = {
  'this-month': {
    label: 'This month',
    window: dateRangeWindows.THIS_MONTH,
  },
  'last-month': {
    label: 'Last month',
    window: dateRangeWindows.LAST_MONTH,
  },
  'this-quarter': {
    label: 'This quarter',
    window: dateRangeWindows.THIS_QUARTER,
  },
  'last-quarter': {
    label: 'Last quarter',
    window: dateRangeWindows.LAST_QUARTER,
  },
  'this-year': {
    label: 'This year',
    window: dateRangeWindows.THIS_YEAR,
  },
  'last-year': {
    label: 'Last year',
    window: dateRangeWindows.LAST_YEAR,
  },
}

export const RANGE_PRESETS = Object.keys(PRESETS).map(id => ({
  id,
  label: PRESETS[id].label,
}))

const formatWindowLabel = (start, end) =>
  start.hasSame(end, 'month')
    ? start.toFormat(WINDOW_LABEL_FORMAT)
    : `${start.toFormat(WINDOW_LABEL_FORMAT)} – ${end.toFormat(
      WINDOW_LABEL_FORMAT,
    )}`

const countMonths = (start, end) =>
  (end.year - start.year) * 12 + end.month - start.month + 1

const buildWindow = ({ id, presetLabel, start, end }) => {
  const first = start.startOf('month')
  const last = end.endOf('month')
  const monthCount = countMonths(first, last)
  const label = formatWindowLabel(first, last)

  return {
    id,
    presetLabel: presetLabel || label,
    label,
    startMonth: first.toFormat(MONTH_FORMAT),
    endMonth: last.toFormat(MONTH_FORMAT),
    startDate: first.toFormat(DATE_FORMAT),
    endDate: last.toFormat(DATE_FORMAT),
    months: Array.from({ length: monthCount }, (_, index) =>
      first.plus({ months: index }).toFormat(MONTH_FORMAT),
    ),
  }
}

export const getPresetWindow = (id, now = DateTime.now()) => {
  const preset = PRESETS[id] || PRESETS[DEFAULT_RANGE_ID]
  const { startDate, endDate } = datesForRelativeWindow(preset.window, now)

  return buildWindow({
    id: PRESETS[id] ? id : DEFAULT_RANGE_ID,
    presetLabel: preset.label,
    start: startDate,
    end: endDate,
  })
}

export const parseMonth = value =>
  /^\d{4}-\d{2}$/.test(value || '')
    ? DateTime.fromFormat(value, MONTH_FORMAT)
    : DateTime.invalid('unparsable month')

// Returns the reason a custom range cannot be used, or null when it can.
export const validateCustomRange = ({ start, end }, now = DateTime.now()) => {
  const startMonth = parseMonth(start)
  const endMonth = parseMonth(end)

  if (!startMonth.isValid || !endMonth.isValid) {
    return 'Choose a start and end month.'
  }
  if (endMonth < startMonth) {
    return 'The end month must be the same as or after the start month.'
  }
  if (endMonth > now.startOf('month')) {
    return 'The end month cannot be in the future.'
  }
  if (
    startMonth <
    now.startOf('month').minus({ months: MAX_CUSTOM_RANGE_LOOKBACK_MONTHS })
  ) {
    return `The start month cannot be more than ${MAX_CUSTOM_RANGE_LOOKBACK_MONTHS} months ago.`
  }
  if (countMonths(startMonth, endMonth) > MAX_CUSTOM_RANGE_MONTHS) {
    return `Choose a range of ${MAX_CUSTOM_RANGE_MONTHS} months or fewer.`
  }

  return null
}

// Returns the window for a valid custom range, otherwise null.
export const getCustomWindow = ({ start, end }, now = DateTime.now()) =>
  validateCustomRange({ start, end }, now)
    ? null
    : buildWindow({
      id: CUSTOM_RANGE_ID,
      start: parseMonth(start),
      end: parseMonth(end),
    })

// The `yyyy-MM` value a month picker's date stands for, or '' when the date is
// missing or still being typed.
export const toMonthValue = date =>
  date && date.isValid ? date.toFormat(MONTH_FORMAT) : ''

// The first and last day a custom range's month pickers can show.
export const getCustomRangeLimits = (now = DateTime.now()) => ({
  minDate: now
    .startOf('month')
    .minus({ months: MAX_CUSTOM_RANGE_LOOKBACK_MONTHS }),
  maxDate: now.endOf('month'),
})

// Reads the selected window from the query string. Missing or invalid params
// fall back to the default window.
export const getRangeFromSearch = (search, now = DateTime.now()) => {
  const params = new URLSearchParams(search)
  const preset = params.get(RANGE_PARAMS.preset)

  if (preset && PRESETS[preset]) return getPresetWindow(preset, now)

  return (
    getCustomWindow(
      {
        start: params.get(RANGE_PARAMS.start),
        end: params.get(RANGE_PARAMS.end),
      },
      now,
    ) || getPresetWindow(DEFAULT_RANGE_ID, now)
  )
}

// Writes a selection (`{ preset }` or `{ start, end }`) into the query string,
// leaving unrelated params alone. The default range is the absence of params.
export const setRangeInSearch = (search, { preset, start, end }) => {
  const params = new URLSearchParams(search)
  Object.values(RANGE_PARAMS).forEach(name => params.delete(name))

  if (preset && preset !== DEFAULT_RANGE_ID) {
    params.set(RANGE_PARAMS.preset, preset)
  } else if (!preset) {
    params.set(RANGE_PARAMS.start, start)
    params.set(RANGE_PARAMS.end, end)
  }

  const query = params.toString()

  return query ? `?${query}` : ''
}

export const fitToMonths = (data, months, dataKey) =>
  months.map(month => ({
    month,
    [dataKey]:
      (data || []).find(point => point.month === month)?.[dataKey] || 0,
  }))

export const sortByMonth = data =>
  [...(data || [])].sort((a, b) => a.month.localeCompare(b.month))

// Compares the month `now` falls in with the month before it. Charts pass the
// last month of the selected window, which is not always the current month.
export const getMonthOverMonth = (data, dataKey, now = DateTime.now()) => {
  const valueFor = month =>
    (data || []).find(point => point.month === month)?.[dataKey] || 0
  const current = valueFor(now.toFormat(MONTH_FORMAT))
  const previous = valueFor(now.minus({ months: 1 }).toFormat(MONTH_FORMAT))

  return {
    current,
    previous,
    deltaPercent:
      previous > 0 ? Math.round(((current - previous) / previous) * 100) : null,
  }
}

// Describes the latest month in the window. When it is the current month the
// copy says "so far this month" and "last month"; otherwise the caller passes
// `monthLabel` and `previousMonthLabel` so the copy names the months instead.
export const getMonthInsight = (
  { current, previous, deltaPercent },
  options,
) => {
  const { subject, format, monthLabel, previousMonthLabel } = options

  if (!current && !previous) return null

  const thisMonth = `${subject} ${format(current)} ${
    monthLabel ? `in ${monthLabel}` : 'so far this month'
  }`
  const lastMonth = previousMonthLabel
    ? `in ${previousMonthLabel}`
    : 'last month'

  if (deltaPercent === null) return `${thisMonth}.`
  if (deltaPercent === 0) {
    return `${thisMonth}, even with ${previousMonthLabel ||
      'last month'} (${format(previous)}).`
  }

  return `${thisMonth}, ${deltaPercent > 0 ? 'up' : 'down'} ${Math.abs(
    deltaPercent,
  )}% from ${format(previous)} ${lastMonth}.`
}

const MIX_CATEGORIES = [
  { id: 'private', label: 'Private', noun: 'Private lessons' },
  { id: 'group', label: 'Group', noun: 'Group events' },
]

export const sumMonthly = (byMonth, dataKey = 'count') =>
  (byMonth || []).reduce((sum, point) => sum + (point[dataKey] || 0), 0)

export const sumMonthlyCounts = byMonth => sumMonthly(byMonth, 'count')

export const buildMixSegments = ({ private: privateValue, group }) => {
  const values = { private: privateValue || 0, group: group || 0 }
  const total = values.private + values.group
  const privatePercent =
    total > 0 ? Math.round((values.private / total) * 100) : 0

  return {
    total,
    segments: MIX_CATEGORIES.map(({ id, label, noun }) => ({
      id,
      label,
      noun,
      value: values[id],
      percent:
        id === 'private'
          ? privatePercent
          : total > 0
            ? 100 - privatePercent
            : 0,
    })),
  }
}

export const getMixInsight = ({ segments, total }) => {
  if (!total) return null

  const [first, second] = segments
  if (first.value === second.value) {
    return `Bookings are split evenly between ${first.noun.toLowerCase()} and ${second.noun.toLowerCase()}.`
  }

  const leader = first.value > second.value ? first : second

  return `${leader.noun} account for ${leader.percent}% of bookings.`
}

export const getRevenueInsight = ({ segments, total }) => {
  if (!total) return null

  const [first, second] = segments
  if (first.value === second.value) {
    return `Revenue is split evenly between ${first.noun.toLowerCase()} and ${second.noun.toLowerCase()}.`
  }

  const leader = first.value > second.value ? first : second

  return `${leader.noun} account for ${leader.percent}% of revenue.`
}
